/**
 * Signed-in session data shared by the shell and pages: /api/me, the user's library (sidebar "Tracked
 * Creators", counts, ⌘K) and credits (usage meter). `refresh()` reloads it and remounts the page.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type LibraryItem, type Me } from './api';

export interface PublicSettings { tiers: { legendary: number; viral: number; outlier: number; above: number }; secondsPerCredit: number; videoRetentionDays: number; saveVideos?: boolean; maintenanceMessage?: string | null; extensionStoreUrl?: string | null }
/** Credits in this account's current period; resetsAt = the account's billing date (decision 85). */
export interface Credits { allowance: number; used: number; available: number; periodStart: number; resetsAt: number; secondsPerCredit: number }
interface SessionValue {
  me: Me;
  library: LibraryItem[] | null;
  credits: Credits | null;
  settings: PublicSettings | null;
  refreshKey: number;
  refresh: () => void;
  reloadLibrary: () => void;
}

const Ctx = createContext<SessionValue | null>(null);
export const useSession = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
};

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [library, setLibrary] = useState<LibraryItem[] | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [settings, setSettings] = useState<PublicSettings | null>(null);
  useEffect(() => { api<PublicSettings>('/api/settings/public').then(setSettings, () => setSettings(null)); }, []);
  const [refreshKey, setRefreshKey] = useState(0);
  const reloadLibrary = useCallback(() => {
    api<{ creators: LibraryItem[] }>('/api/library').then((r) => setLibrary(r.creators), () => setLibrary([]));
  }, []);
  const loadCredits = useCallback(() => {
    api<Credits>('/api/credits').then(setCredits, (e) => !(e instanceof ApiError && e.status === 401) && setCredits(null));
  }, []);
  useEffect(() => {
    reloadLibrary();
    loadCredits();
  }, [refreshKey]);
  // Back to this tab (e.g. after scanning in the extension): the library and credits are reloaded, at most once a minute.
  useEffect(() => {
    let last = Date.now();
    const onFocus = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 60_000) return;
      last = Date.now();
      reloadLibrary();
      loadCredits();
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [reloadLibrary, loadCredits]);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  return <Ctx.Provider value={{ me, library, credits, settings, refreshKey, refresh, reloadLibrary }}>{children}</Ctx.Provider>;
}
